import { supabase, unwrap } from '../supabase';
import { config } from '../config';

export const MCP_URL = `${config.mcpApi}/connect`;
export const REST_URL = `${config.mcpApi}/api/v1`;

export interface ConnectedApp {
  id: string;
  name: string;
  connectedAt: string;
  scopes: string[];
}

export interface ApiRequest {
  id: number;
  method: string;
  path: string;
  status: number;
  durationMs: number;
  source: string;
  createdAt: string;
}

export async function listApiRequests(orgId: string): Promise<{ available: boolean; rows: ApiRequest[] }> {
  const result = await supabase
    .from('api_request_logs')
    .select('id,method,path,status,duration_ms,source,created_at')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(20);
  // Older deployments have no request logging yet. Do not invent activity.
  if (result.error && ['PGRST205', '42P01'].includes(result.error.code))
    return { available: false, rows: [] };
  const rows = unwrap(result) || [];
  return {
    available: true,
    rows: rows.map((row) => ({
      id: row.id,
      method: row.method,
      path: row.path,
      status: row.status,
      durationMs: row.duration_ms,
      source: row.source,
      createdAt: row.created_at,
    })),
  };
}

async function ownerId() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data.user) throw new Error('Please sign in again.');
  return data.user.id;
}

/** One key belongs to the organization; never create a second key row. */
export async function getApiKey(orgId: string): Promise<string | null> {
  const userId = await ownerId();
  const row = unwrap<{ api_key: string | null }>(
    await supabase
      .from('organizations')
      .select('api_key')
      .eq('org_id', orgId)
      .eq('creator_id', userId)
      .single(),
  );
  if (!row) throw new Error('Only the organization owner can manage its API key.');
  return row.api_key || null;
}

export async function replaceApiKey(orgId: string, previousKey: string | null): Promise<string> {
  const userId = await ownerId();
  const org = unwrap<{ has_webhook_access: boolean; plan_expiry_date: string | null }>(
    await supabase
      .from('organizations')
      .select('has_webhook_access,plan_expiry_date')
      .eq('org_id', orgId)
      .eq('creator_id', userId)
      .single(),
  );
  if (!org) throw new Error('Only the organization owner can manage its API key.');
  if (!org.has_webhook_access || !org.plan_expiry_date || Date.parse(org.plan_expiry_date) <= Date.now()) {
    throw new Error('Enable integrations on an active plan to use the API.');
  }
  const key = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(24))));
  let update = supabase
    .from('organizations')
    .update({ api_key: key })
    .eq('org_id', orgId)
    .eq('creator_id', userId);
  update = previousKey ? update.eq('api_key', previousKey) : update.is('api_key', null);
  const rows = unwrap(await update.select('api_key'));
  if (rows.length !== 1) throw new Error('The key changed elsewhere. Refresh this page and try again.');
  return rows[0].api_key;
}

/** OAuth grants belong to the signed-in account, not the workspace metadata. */
export async function listConnectedApps(): Promise<ConnectedApp[]> {
  if (config.isSandbox && !config.oauthServerEnabled) return [];
  const { data, error } = await supabase.auth.oauth.listGrants();
  if (error) throw error;
  return (data || []).map((grant) => ({
    id: grant.client.id,
    name: grant.client.name || 'Connected app',
    connectedAt: grant.granted_at,
    scopes: grant.scopes,
  }));
}

export async function disconnectApp(clientId: string): Promise<void> {
  const { error } = await supabase.auth.oauth.revokeGrant({ clientId });
  if (error) throw error;
}
